import React, { useState, useMemo, ChangeEvent } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import { useNotification } from '../../../../../../contexts/NotificationsContext';
import SearchBar from '../../../../../components/SearchBar';
import Card from '../../../../../../muiComponents/Card';
import Title from '../../../Title';
import useTranslate from '../../../../../../customHooks/useTranslate';
import Button from '../../../../../../muiComponents/Button';
import {
    usePropertyFetching,
    usePropertyPresentation,
    usePropertyRoomDetail,
} from '../../../../../contextsProperty/ContextProperties';
import RoomItem from './RoomItem';
import LoadingPlaceholder from '../../../../../../muiComponents/LoadingPlaceholder';
import {
    DDBB_NEW_ROOM_INITIAL_STATE,
    getVisibleDescription,
    ROOM_INITIAL_STATE,
    URL_HOTEL_BASE,
    URL_INVENTORY_LIST,
    URL_TAB_PARAM,
} from '../../../../../../utils/property/propertyUtils';
import convertToOvariant from '../../../../../../utils/convertToOvariant';
import { savableEntities } from '../../../../../../utils/commonVariables/entityKeys';
import api from '../../../../../../services/api';
import endpoints from '../../../../../../utils/commonVariables/endpoints';
import useUserProfile from '../../../../../../customHooks/useUserProfile';
import encodeBase64 from '../../../../../../utils/encodeBase64';
import useLanguage from '../../../../../../customHooks/useLanguage';
import { FeedbackMessages } from '../../../../../../utils/commonVariables/messages';

const RoomList = () => {
    const [searchValue, setSearchValue] = useState('');
    const { readOnlyUser } = useUserProfile();
    const { rooms, idSupplierHotel, taxes, supplements, stopSales } =
        usePropertyPresentation();
    const { handleFetching, isFetching } = usePropertyFetching();
    const { addNewRoom, handleTargetRoom } = usePropertyRoomDetail();
    // old code => refactor in new context
    const { setErrorMessage } = useNotification();
    const appLanguage = useLanguage();

    const translate = useTranslate();
    const history = useHistory();
    const buttonText = translate('add_room');
    const searcherPlaceholder = translate('search_room');
    const headerRoom = translate('room');
    const headerServices = translate('services');
    const headerBedType = translate('bed_types');
    const noResultsText = translate('no_results');

    const filteredRooms = useMemo(() => {
        return rooms.filter(room => {
            const visibleDescription = getVisibleDescription(
                room.descriptions,
                appLanguage
            );
            return (
                room.name.toLowerCase().includes(searchValue.toLowerCase()) ||
                (visibleDescription
                    ? visibleDescription?.descriptionContent
                          .toLowerCase()
                          .includes(searchValue.toLowerCase())
                    : false)
            );
        });
    }, [rooms, searchValue]);

    async function handleCreateNewRoom() {
        handleFetching(true);
        const newRoom = DDBB_NEW_ROOM_INITIAL_STATE;
        newRoom.IdSupplierHotel = `${idSupplierHotel}`;
        const objectToSend = convertToOvariant(
            savableEntities.SupplierHotelRoomType,
            newRoom,
            '-1'
        );
        try {
            const responseSave = await api.post(
                endpoints.commonSave,
                objectToSend
            );

            const newRoomState = {
                ...ROOM_INITIAL_STATE,
                id: responseSave.data.Id,
                name: responseSave.data.Name,
            };
            addNewRoom(newRoomState);
            handleTargetRoom(newRoomState.id);
            history.push(
                `${URL_HOTEL_BASE(
                    idSupplierHotel
                )}${URL_INVENTORY_LIST}/${encodeBase64(
                    responseSave.data.Id
                )}${URL_TAB_PARAM(1)}`
            );
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            handleFetching(false);
        }
    }

    return (
        <section className='flex-column gap-10'>
            <div className='flex flex-between'>
                {isFetching ? (
                    <LoadingPlaceholder
                        variant='text'
                        height={24}
                        width='100%'
                    />
                ) : (
                    <Title text='room_configuration' />
                )}
            </div>

            <div className='flex gap-20 flex-align-center'>
                {isFetching ? (
                    <LoadingPlaceholder
                        variant='text'
                        height={32}
                        width='100%'
                    />
                ) : (
                    <>
                        <SearchBar
                            value={searchValue}
                            placeholder={searcherPlaceholder}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setSearchValue(e?.target?.value || '')
                            }
                            onClear={() => setSearchValue('')}
                        />
                        <Button
                            variant='contained'
                            className='white-space-nowrap'
                            style={{ height: '30px' }}
                            onClick={handleCreateNewRoom}
                            disabled={readOnlyUser}
                        >
                            {buttonText}
                        </Button>
                    </>
                )}
            </div>
            <Card className='padding-inline-20 overflow-x-auto'>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell style={{ textTransform: 'capitalize' }}>
                                {headerRoom}
                            </TableCell>
                            <TableCell>{headerServices}</TableCell>
                            <TableCell>{headerBedType}</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRooms.map(room => {
                            const taxesCount = taxes.filter(
                                tax => tax.idRoom === parseInt(room.id)
                            ).length;
                            const supplementsCount = supplements.filter(
                                supplement =>
                                    supplement.idRoom === parseInt(room.id)
                            ).length;
                            const stopSalesCount = stopSales.filter(
                                stopSale =>
                                    stopSale.idRoom === parseInt(room.id)
                            ).length;
                            return (
                                <RoomItem
                                    key={room.id}
                                    room={room}
                                    taxesCount={taxesCount}
                                    supplementsCount={supplementsCount}
                                    stopSalesCount={stopSalesCount}
                                />
                            );
                        })}
                        {!filteredRooms.length && !isFetching && (
                            <TableRow>
                                <TableCell
                                    colSpan={4}
                                    style={{ textAlign: 'center' }}
                                >
                                    {noResultsText}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Card>
        </section>
    );
};

export default RoomList;
